import "./TransactionSummaryDialog.css";
import { Alert, Button, DetailList, Dialog } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import { bankName, date, money } from "../../workspace/model";
import { statusBadge } from "../../workspace/shared";
import { paymentCardLabel } from "../paymentCardLabel";

export interface TransactionSummaryDialogProps {
  readonly open: boolean;
  readonly detail?: Schemas["TransactionAdminDetailView"];
  readonly state: "loading" | "error" | "ready";
  readonly onClose: () => void;
  readonly onRetry: () => void;
  readonly action?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly busy?: boolean;
}

const stages = ["심사 중", "결제 대기", "결제 완료", "송금 대기", "거래 완료"];
export function TransactionSummaryDialog({ open, detail, state, onClose, onRetry, action, children, busy = false }: TransactionSummaryDialogProps) {
  const t = detail?.transaction;
  const step = t?.status === "COMPLETED" ? 4 : t && ["TRANSFER_READY", "TRANSFER_PROCESSING", "TRANSFER_FAILED"].includes(t.status) ? 3 : t?.paymentStatus === "APPROVED" ? 2 : t?.status === "PAYMENT_PENDING" ? 1 : 0;
  const uncertain = Boolean(t && (t.status.includes("UNKNOWN") || ["PAYMENT_CANCELING", "PAYMENT_CANCELLATION_PENDING"].includes(t.status) || ["PROCESSING", "UNKNOWN"].includes(detail?.cancellationStatus ?? "") || t.paymentStatus === "UNKNOWN" || t.transferStatus === "UNKNOWN"));
  const interrupted = uncertain || Boolean(t && ["CANCELED", "REJECTED", "TRANSFER_FAILED"].includes(t.status));
  return <Dialog open={open} title={`거래 상세${t?.displayTransactionNo ? ` · ${t.displayTransactionNo}` : ""}`} onClose={onClose} closeDisabled={busy} size="medium" primaryAction={action} secondaryAction={<Button size="small" variant="quiet" onClick={onClose} disabled={busy}>닫기</Button>}>
    {state === "loading" ? <p role="status">거래 정보를 불러오는 중입니다.</p> : state === "error" ? <Alert persistent variant="danger" title="거래 정보를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}>접근 권한과 연결 상태를 확인해 주세요.</Alert> : t ? <>
      <ol className="admin-transaction-steps" aria-label="거래 진행 단계">{stages.map((label, index) => <li key={label} aria-current={index === step ? "step" : undefined} data-completed={index < step || index === 4 && t.status === "COMPLETED"}><span>{index < step || index === 4 && t.status === "COMPLETED" ? "✓ " : ""}{label}</span></li>)}</ol>
      {interrupted ? <Alert persistent variant={uncertain ? "warning" : "information"} title="거래 진행 상태">{statusBadge(t.status)}{uncertain ? " · 처리 결과를 먼저 확인해 주세요. 같은 결제나 송금을 다시 요청하지 마세요." : null}</Alert> : null}
      <DetailList items={[
        { label: "현재 거래 상태", value: statusBadge(t.status) },
        { label: "납부금액", value: money(t.approvedAmount ?? t.requestedAmount) },
        { label: "입금 계좌", value: `${detail.recipientBankName ?? bankName(t.recipientBankCode)} · ${t.recipientAccountNumberMasked}` },
        { label: "예금주", value: t.recipientHolderMasked ?? "정보 없음" },
        { label: "결제 카드", value: paymentCardLabel(detail) },
        { label: "결제일시", value: t.paymentApprovedAt ? date(t.paymentApprovedAt) : "결제 전" },
        { label: t.transferCompletedAt ? "송금 완료일시" : "송금 예정일시", value: t.transferCompletedAt ? date(t.transferCompletedAt) : t.transferScheduledAt ? date(t.transferScheduledAt) : "미선택" },
        { label: "원거래번호", value: t.transactionNo },
      ]} />
      {children}
    </> : null}
  </Dialog>;
}
